import { useEffect, useState } from "react";
import { useTenant } from "@/lib/tenant";
import { Plus, Edit3, Trash2, X, Save, Loader2, ToggleLeft, ToggleRight, Tractor, Radio } from "lucide-react";
import { toast } from "sonner";

export default function Machines() {
  const { api } = useTenant();
  const [manual, setManual] = useState([]);
  const [live, setLive] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/admin/machines"),
      api.get("/fieldops/fleet").catch(() => ({ data: { data: [] } })),
    ]).then(([m, f]) => {
      setManual(m.data || []);
      const fp = f.data;
      setLive(Array.isArray(fp) ? fp : (fp?.data || []));
    }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const toggle = async (m) => {
    try { await api.patch(`/admin/machines/${m.id}`, { active: !m.active }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };
  const onDelete = async (m) => {
    if (!window.confirm(`Delete ${m.label}?`)) return;
    try { await api.delete(`/admin/machines/${m.id}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="p-8 max-w-5xl">
      <h1 className="text-2xl font-bold mb-1">Machines</h1>
      <p className="text-sm text-slate-400 mb-6">
        The operator app shows <strong className="text-emerald-300">live</strong> tractors from New Holland FieldOps
        plus any <strong className="text-amber-300">manual</strong> machines you register below (for tractors not on FieldOps).
      </p>

      {/* Live machines */}
      <section className="mb-8">
        <div className="flex items-center gap-2 mb-3">
          <Radio size={14} className="text-emerald-400" />
          <h2 className="text-sm uppercase tracking-widest text-slate-400">Live from FieldOps</h2>
          <span className="text-xs text-slate-500">({live.length})</span>
        </div>
        <div className="bg-[#0a0e1c] border border-white/10 rounded-lg p-4">
          {live.length === 0 ? (
            <div className="text-slate-500 text-sm">No live machines yet — connect FieldOps in <code className="text-amber-300">/admin/fleet</code>.</div>
          ) : (
            <ul className="space-y-2">
              {live.map(m => (
                <li key={m.vin} className="flex items-center gap-3 text-sm">
                  <Tractor size={16} className="text-emerald-400 shrink-0" />
                  <div className="flex-1">
                    <span className="font-medium">{m.nickname || m.vin}</span>
                    <span className="text-slate-400 ml-2">{m.model}</span>
                  </div>
                  <span className="text-[10px] uppercase tracking-widest text-emerald-300">Live</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* Manual machines */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Tractor size={14} className="text-amber-400" />
            <h2 className="text-sm uppercase tracking-widest text-slate-400">Manual machines</h2>
            <span className="text-xs text-slate-500">({manual.length})</span>
          </div>
          <button onClick={() => setShowAdd(true)} data-testid="add-machine"
                  className="bg-amber-500 hover:bg-amber-400 text-[#0a0e1c] font-bold px-3 py-1.5 rounded-lg flex items-center gap-1.5 text-sm">
            <Plus size={14} /> Add machine
          </button>
        </div>

        {loading ? (
          <div className="text-slate-500 flex items-center gap-2"><Loader2 className="animate-spin" size={14} /> Loading…</div>
        ) : (
          <div className="bg-[#0a0e1c] border border-white/10 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-white/[0.03] text-[10px] uppercase tracking-widest text-slate-400">
                <tr>
                  <th className="text-left px-4 py-3">Label</th>
                  <th className="text-left px-4 py-3">Model</th>
                  <th className="text-left px-4 py-3">VIN / Reg</th>
                  <th className="text-left px-4 py-3">Active</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {manual.map(m => (
                  <tr key={m.id} className="border-t border-white/5 hover:bg-white/[0.02]">
                    <td className="px-4 py-3 font-medium">{m.label}</td>
                    <td className="px-4 py-3 text-slate-400">{m.model || "—"}</td>
                    <td className="px-4 py-3 text-slate-500 font-mono text-xs">{m.vin}</td>
                    <td className="px-4 py-3">
                      <button onClick={() => toggle(m)} className={`flex items-center gap-1 text-xs font-bold ${m.active ? "text-emerald-300" : "text-slate-500"}`}>
                        {m.active ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
                        {m.active ? "ON" : "OFF"}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button onClick={() => setEditing(m)} className="text-slate-300 hover:text-amber-300 p-1.5"><Edit3 size={14} /></button>
                      <button onClick={() => onDelete(m)} className="text-slate-300 hover:text-red-300 p-1.5"><Trash2 size={14} /></button>
                    </td>
                  </tr>
                ))}
                {!manual.length && (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">No manual machines yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {(showAdd || editing) && (
        <MachineModal m={editing}
                      onClose={() => { setShowAdd(false); setEditing(null); }}
                      onSaved={() => { setShowAdd(false); setEditing(null); load(); }}
                      api={api} />
      )}
    </div>
  );
}

function MachineModal({ m, onClose, onSaved, api }) {
  const editing = !!m;
  const [label, setLabel] = useState(m?.label || "");
  const [model, setModel] = useState(m?.model || "");
  const [vin, setVin]     = useState(m?.vin || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState(null);

  const save = async () => {
    if (!label.trim()) { setErr("Label is required"); return; }
    setBusy(true); setErr(null);
    try {
      if (editing) {
        await api.patch(`/admin/machines/${m.id}`, { label, model, vin });
      } else {
        await api.post("/admin/machines", { label, model, vin });
      }
      onSaved();
    } catch (e) {
      setErr(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm grid place-items-center z-50 p-4" onClick={onClose}>
      <div className="bg-[#0b1020] border border-white/10 rounded-xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-bold text-lg">{editing ? "Edit machine" : "Add machine"}</h3>
          <button onClick={onClose}><X size={18} className="text-slate-400" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-xs uppercase tracking-widest text-slate-400 mb-1.5">Label *</label>
            <input value={label} onChange={e => setLabel(e.target.value)} placeholder="e.g. T7.270" data-testid="machine-label"
                   className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest text-slate-400 mb-1.5">Model</label>
            <input value={model} onChange={e => setModel(e.target.value)} placeholder="e.g. New Holland T7.270"
                   className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest text-slate-400 mb-1.5">VIN / Reg <span className="text-slate-500 normal-case">(optional)</span></label>
            <input value={vin} onChange={e => setVin(e.target.value)} placeholder="VIN or registration plate"
                   className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white font-mono text-sm" />
          </div>
          {err && <div className="text-sm text-red-300">{err}</div>}
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="flex-1 px-4 py-2 rounded-lg bg-white/5 text-slate-300">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="machine-save"
                  className="flex-1 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-[#0a0e1c] font-bold px-4 py-2 rounded-lg flex items-center justify-center gap-1.5">
            {busy ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Save
          </button>
        </div>
      </div>
    </div>
  );
}
